<template>
  <AmountStep
    v-if="state.matches('refundFlow.amountStep')"
    :customerRequestId
    :partial-refund-amount="partialRefundPrice?.amount"
    :refund-info
    :send
    @done="handleRefundDone(false)"
    @save-partial-refund-amount="
      (amount) => (partialRefundPrice.amount = amount)
    "
  >
    <template #before-form>
      <RefundWarning
        :hasActiveInsurancePolicy
        :isPartialRefundAllowed="refundInfo.isPartialRefundAllowed"
        :maxRefundPrice="refundInfo.maxRefundPrice"
        :orderlineQuantity
      />
    </template>
    <NextTaskCheckbox v-if="hasNextTask" v-model="isNextTaskChecked" />
  </AmountStep>

  <PartialRefundStep
    v-if="state.matches('refundFlow.partialRefundStep')"
    :customerRequestId
    :price="partialRefundPrice"
    :send
    @done="handleRefundDone(true)"
  >
    <template #before-form>
      <RefundWarning
        :hasActiveInsurancePolicy
        :isPartialRefundAllowed="refundInfo.isPartialRefundAllowed"
        :maxRefundPrice="refundInfo.maxRefundPrice"
        :orderlineQuantity
      />
    </template>
    <NextTaskCheckbox v-if="hasNextTask" v-model="isNextTaskChecked" />
  </PartialRefundStep>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import type { ProductReturnState } from '~/api/care-folders/types/care-folder'
import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { fetchAndGoToNextTask } from '~/scopes/customer-care/components/request.helpers'
import { useFormatDeadline } from '~/scopes/customer-care/pages/customer-care/composables/useFormatDeadline'

import type {
  MachineSend,
  MachineState,
  RefundInfo,
} from '../../ActionBar.machine.type'
import NextTaskCheckbox from '../NextTaskCheckbox/NextTaskCheckbox.vue'

import AmountStep from './AmountStep/AmountStep.vue'
import PartialRefundStep from './PartialRefundStep/PartialRefundStep.vue'
import RefundWarning from './components/RefundWarning/RefundWarning.vue'

const tracking = useTracking()
const { openErrorToast } = useTheToast()

const emit = defineEmits(['done'])

const props = defineProps<{
  customerRequestId: number
  mostUrgentTask?: OrderlineOpenTask
  refundInfo: RefundInfo
  hasActiveInsurancePolicy: boolean
  orderlineQuantity: number
  productReturnState: ProductReturnState
  send: MachineSend
  state: MachineState
}>()

const partialRefundPrice = ref<MonetaryAmount>({
  amount: '',
  currency: props.refundInfo.maxRefundPrice.currency,
})

const hasNextTask = computed(() => Boolean(props.mostUrgentTask))

const isNextTaskChecked = ref(hasNextTask.value)

function handleRefundDone(isPartial: boolean) {
  if (isNextTaskChecked.value)
    fetchAndGoToNextTask(props.mostUrgentTask?.id).catch(() => {
      openErrorToast()
    })
  tracking.trackFormSubmit({
    name: isPartial ? 'send-partial-refund' : 'send-refund',
    zone: 'asp-action-bar-refund-step',
    customerRequestId: props.customerRequestId,
    productReturnState: props.productReturnState,
    mostUrgentDeadline: props.mostUrgentTask
      ? useFormatDeadline(
          props.mostUrgentTask.deadline,
          props.mostUrgentTask.id,
        )
      : '',
    mostUrgentTaskType: props.mostUrgentTask?.status || '',
    pageType: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
    goToNextTask: isNextTaskChecked.value,
  })
  emit('done')
}
</script>
